import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration, Row } from '../lib/utils';
import { RegularText } from '../lib/Text';
import { useReservationRules, useUpdateReservationRules } from '@/features/reservationRules/hooks';

/**
 * 그룹수업 합산 토글 — 센터 단위 ReservationRules.includeGroupClassInSettlement 를 ON/OFF.
 *   매출 정산·엑셀, 미수금, 신규/재등록 결제, 재등록률(분모/분자), 그리고 피드 대시보드의
 *   일지 작성률(상단 센터 평균 카드 + 지표별 강사 순위) 등 그룹수업 포함 여부에 영향.
 *   토글 변경 시 모든 쿼리 invalidate 하여 화면 즉시 갱신.
 */
const GroupClassToggle = () => {
    const authObject = getItemWithExpiration('authObject');
    const centerId = authObject?.centerId;
    const queryClient = useQueryClient();

    const { data: rules, isLoading } = useReservationRules({ centerId });
    const updateMutation = useUpdateReservationRules();

    if (!centerId) return null;

    const value = !!rules?.includeGroupClassInSettlement;
    const disabled = isLoading || updateMutation.isLoading;
    const helperText = value
        ? '그룹수업 합산 ON: 정산·엑셀과 일지 작성률(지표별 강사 순위)에 그룹수업이 포함됩니다.'
        : '그룹수업 합산 OFF: 정산·엑셀과 일지 작성률(지표별 강사 순위)에서 그룹수업이 제외됩니다.';

    const handleToggle = async () => {
        if (disabled || !rules) return;
        await updateMutation.mutateAsync({
            centerId,
            body: { includeGroupClassInSettlement: !value },
        });
        queryClient.invalidateQueries();
    };

    return (
        <Row
            className="rounded-[10px] justify-center items-center flex-row flex px-[14px] h-[45px]"
            style={{
                backgroundColor: value ? '#065F46' : '#2D3132',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.7 : 1,
            }}
            onClick={handleToggle}
            title={helperText}
            aria-label={helperText}
        >
            <div
                style={{
                    width: 34,
                    height: 18,
                    borderRadius: 9,
                    backgroundColor: value ? '#10B981' : '#6B7280',
                    position: 'relative',
                    marginRight: 10,
                    transition: 'background-color 0.2s',
                    flexShrink: 0,
                }}
            >
                <div
                    style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        position: 'absolute',
                        top: 2,
                        left: value ? 18 : 2,
                        transition: 'left 0.2s',
                    }}
                />
            </div>
            <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="white">
                그룹수업 합산 {value ? 'ON' : 'OFF'}
            </RegularText>
        </Row>
    );
};

export default GroupClassToggle;
